import { Checkbox, LineClamp } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { ChangeEvent, useState } from "react";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { IStandard } from "@/search/Langston/types/IStandard";
import {
  checkboxCss,
  gradeTextCss,
  listItemCss,
  standardDescriptionCss,
} from "../../StandardsModal.styles";
import { getGradesText } from "./StandardsModalCheckbox.utils";

// Custom hook to safely use search page context
const useSafeSearchPageContext = () => {
  try {
    return useSearchPageContext();
  } catch {
    return null;
  }
};

const CheckboxWithTrackEvent = withTrackEvent(Checkbox);

interface StandardsModalCheckboxProps {
  standard: IStandard;
  disabled?: boolean;
  temporarySelectedIds?: string[] | undefined;
  setTemporarySelectedIds?: ((standards: string[]) => void) | undefined;
  setDisplayErrorBanner?: ((value: boolean) => void) | undefined;
}

export const StandardsModalCheckbox = ({
  standard,
  disabled = false,
  temporarySelectedIds,
  setTemporarySelectedIds,
  setDisplayErrorBanner,
}: StandardsModalCheckboxProps) => {
  const {
    updateSelectedStandardsOnChange,
    getTemporarySelectedFilters,
    selectedStandards,
  } = useStandardsSearchContext();

  // This try-catch is needed because the StandardsModal is currently coupled to the search page
  // and uses useSearchPageContext. When the StandardsModal is used outside of the search page
  // (like in the lesson planner), this context won't be available and will throw an error.
  // TODO: We should decouple the StandardsModal from the search page to avoid this coupling.
  const searchPageContext = useSafeSearchPageContext();

  const hasSearchParamValue =
    searchPageContext?.handlers?.hasSearchParamValue || (() => false);

  const standardTitle = `${standard.name}`;
  const grades = standard.grade_levels;
  const gradesText = getGradesText(grades);

  const [isChecked, setIsChecked] = useState<boolean>(() => {
    // Check if standard is in URL params
    const isInUrlParams = hasSearchParamValue(
      LangstonParameterSchema.standards.key,
      standard.id,
    );

    // Check if standard is in temporary selected filters
    const temporarySelectedFilters = getTemporarySelectedFilters();
    const isInTemporaryFilters = temporarySelectedFilters.some(
      (tempStandard) => tempStandard.id === standard.id,
    );

    // Check if standard is in selected standards
    const isInSelectedStandards = selectedStandards.some(
      (selectedStandard) => selectedStandard.id === standard.id,
    );

    return isInUrlParams || isInTemporaryFilters || isInSelectedStandards;
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (setDisplayErrorBanner) setDisplayErrorBanner(disabled);

    if (!disabled) {
      const standardParsed = JSON.parse(e.target.value);

      updateSelectedStandardsOnChange(e.target.checked, standardParsed);
      setIsChecked(e.target.checked);

      if (temporarySelectedIds && setTemporarySelectedIds) {
        if (e.target.checked) {
          setTemporarySelectedIds([...temporarySelectedIds, standardParsed.id]);
        } else {
          setTemporarySelectedIds(
            temporarySelectedIds.filter((id) => id !== standardParsed.id),
          );
        }
      }
    }
  };

  return (
    <li css={listItemCss(disabled)}>
      <CheckboxWithTrackEvent
        __cssFor={checkboxCss}
        label={standardTitle}
        value={JSON.stringify(standard)}
        checked={!disabled ? isChecked : false}
        onChange={handleChange}
        objectType={objectTypes.content}
        actionName={actionNames.filter}
        streamProperties={{ description: "Select standard for search query" }}
        dataProps={{
          "data-qa-selector": `search-standard-${JSON.stringify(standard)}`,
        }}
      />
      {grades.length ? <div css={gradeTextCss}>{gradesText}</div> : null}
      <LineClamp
        lines={1}
        text={standard.description || ""}
        innerElement="p"
        css={standardDescriptionCss}
      />
    </li>
  );
};
